import {
  ProfilesPublicApi,
  ProfileViewSource,
  type ResolveProfilePublicHashDto,
} from '@up2stay/up2promo-api-client';

/**
 * Resolves a digital profile `publicHash` for Cap / LinkResolve.
 * Hits `GET /api/v1/profiles-public/hash/:hash` (records ProfileView with `src`).
 */
export type ResolvedProfilePublicHash = ResolveProfilePublicHashDto;

const PROFILE_VIEW_SOURCE_VALUES: readonly string[] =
  Object.values(ProfileViewSource);

/** Backend re-normalizes anyway; keep the client type honest for the enum-typed query param. */
function normalizeSrc(src: string): ProfileViewSource {
  const trimmed = src.trim().toLowerCase();
  if (trimmed === 'qr' || trimmed === 'profile-qr') {
    return ProfileViewSource.PROFILE_QR;
  }
  return (PROFILE_VIEW_SOURCE_VALUES as readonly string[]).includes(trimmed)
    ? (trimmed as ProfileViewSource)
    : ProfileViewSource.UNKNOWN;
}

export async function resolveProfilePublicHash(
  hash: string,
  src?: string | null,
): Promise<ResolvedProfilePublicHash | null> {
  const trimmed = hash.trim();
  if (!trimmed) return null;

  try {
    const res = await ProfilesPublicApi.resolveByPublicHash({
      path: { hash: trimmed },
      query: src?.trim() ? { src: normalizeSrc(src) } : undefined,
    });
    const data = res.data;
    if (
      !data?.workspaceUsername?.trim() ||
      !data?.username?.trim() ||
      !data?.profileId?.trim()
    ) {
      return null;
    }
    return data;
  } catch (err: unknown) {
    const ax = err as { response?: { status?: number } };
    if (ax.response?.status === 404) return null;
    throw err;
  }
}
